import { Box, Grid, Button, Tooltip, Popper } from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useMemo,
} from 'react';
import { useDispatch } from 'react-redux';

import CustomFilter from 'common/shared-ui/src/components/CustomFilter';
import { getTranslatedText } from 'common/utils/getTranslatedText';

import {
  getResourcePlanFilters,
  getResourcePlanFiltersQuery,
} from '../../../../packages/common/api';
import DoneIcon from '../../../../packages/common/shared-ui/src/icons/DoneIcon';
import { useTranslation } from '../../../../packages/common/utils/getLocalString';
import {
  clearResplanTableRows,
  setResplanTableRows,
  setStopLoadingRespanTableBySocket,
} from '../../app/redux/reducers/userSlice';

// Original class: filterTopRow
const StyledFilterTopRow = styled(Box)(theme => ({
  display: 'flex',
  width: '190px',
  verticalAlign: 'top',

  '& .MuiAutocomplete-input': {
    position: 'relative',
  },

  '& .autoCompletePopper': {
    position: 'relative',
  },

  '& defaultFirstStepPaper': {
    position: 'relative',
  },

  '& .MuiFormControl-root': {
    margin: '0px',
  },
}));
// Original class: filterWrap
const StyledFilterWrap = styled(Box)(theme => ({
  display: 'inline-flex !important',
  width: '177px',
  verticalAlign: 'top',

  '& .MuiAutocomplete-input': {
    position: 'relative',
  },

  '& .autoCompletePopper': {
    position: 'relative',
  },

  '& defaultFirstStepPaper': {
    position: 'relative',
  },

  '& .MuiFormControl-root': {
    margin: '0px',
  },
}));
// Original class: commentTooltip
const StyledCommentTooltip = styled(Popper)(({ theme }) => ({
  '& .MuiTooltip-tooltip': {
    marginTop: '0 !important',
    marginBottom: '3px !important',
    backgroundColor: '#F1F2F8',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    color: '#212346',
    boxShadow: '0px 5px 10px -8px',
    fontSize: '12px',
  },
}));
// Original class: confirmButton
const StyledConfirmButton = styled(Button)(theme => ({
  minWidth: '40px',
  height: '40px',
  padding: '8px 8px 8px',
  backgroundColor: '#DCDFF4',
  borderRadius: '12px',
  fill: '#7174AC',
  '& .MuiSvgIcon-root path': {
    transition:
      'fill 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  '&:hover': {
    backgroundColor: '#6C7AD9',
    color: '#ffffff',
    fill: '#ffffff !important',
    '& .MuiSvgIcon-root path': {
      fill: '#ffffff !important',
    },
  },
  '&:focus': {
    backgroundColor: '#7E8BE6',
    color: '#ffffff',
    fill: '#ffffff !important',
    '& .MuiSvgIcon-root path': {
      fill: '#ffffff !important',
    },
  },
}));
const ResourcePlanFilter = ({
  filters,
  setQuery,
  topFilters = 3,
  setFetchedQuery,
  query,
  fetchedQuery,
  refreshKey,
}) => {
  const getLocalString = useTranslation();
  const [topFiltersList, setTopFiltersList] = useState([]);
  const [restFiltersList, setRestFiltersList] = useState([]);
  const [hasNewFilters, setHasNewFilters] = useState(false);
  const [, forceUpdate] = useReducer(x => x + 1, 0);
  const applyTooltip = getLocalString('resourcePlan', 'labels', 'applyTooltip');
  const dispatch = useDispatch();

  useEffect(() => {
    const fieldPriority = [
      'resourcePlan.group_by',
      'resourcePlan.date_from',
      'resourcePlan.date_to',
      'resourcePlan.business_unit',
      'issue.project_list',
      'resourcePlan.contract_issues',
      'resourcePlan.department',
      'resourcePlan.user_list',
      'resourcePlan.issues',
    ];
    let newModifiedFilters = filters.map(filter => {
      if (filter.fieldId === 'resourcePlan.date_to') {
        return { ...filter, fieldType: 'resPlan_date_to' };
      } else if (filter.fieldId === 'resourcePlan.date_from') {
        return { ...filter, fieldType: 'resPlan_date_from' };
      } else {
        return filter;
      }
    });

    newModifiedFilters.sort((a, b) => {
      const indexA = fieldPriority.indexOf(a.fieldId);
      const indexB = fieldPriority.indexOf(b.fieldId);

      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    });

    setTopFiltersList(newModifiedFilters.slice(0, topFilters));
    setRestFiltersList(newModifiedFilters.slice(topFilters));
  }, [filters, topFilters]);

  useEffect(() => {
    const isQueryNew = query !== fetchedQuery;
    setHasNewFilters(isQueryNew);
  }, [query, fetchedQuery]);
  const parseQuery = queryStr => {
    const items = queryStr.split('$$');
    const parsed = {};

    items.forEach(item => {
      const [key, operator, value] = item.split(';');
      let values = '';
      if (value) {
        values = value.includes('||') ? value.split('||') : [value];
      }
      parsed[key] = {
        operator,
        value: values,
      };
    });
    return parsed;
  };

  const parsedQuery = useMemo(() => parseQuery(query), [query]);

  const [filterValues, setFilterValues] = useState({
    chosenFirstStepItems: {
      'resourcePlan.group_by': {
        valueName: 'GroupBy',
        type: 'eq',
        fieldType: 'group_by',
        subLabels: [
          {
            valueId: parsedQuery['resourcePlan.group_by'].value,
            isChecked: true,
          },
        ],
      },
      'resourcePlan.date_from': {
        valueName: 'DateFrom',
        type: 'gte',
        label: 'DateFrom',
        notParsedType: 'gte',
        fieldType: 'date',
        value: 'resourcePlan.date_from',
        subLabels: [
          {
            valueName: new Date(
              parsedQuery['resourcePlan.date_from'].value,
            ).toLocaleDateString(),
            valueId: parsedQuery['resourcePlan.date_from'].value,
          },
        ],
      },
      'resourcePlan.date_to': {
        valueName: 'DateTo',
        type: 'lte',
        label: 'DateTo',
        notParsedType: 'lte',
        fieldType: 'date',
        value: 'resourcePlan.date_to',
        subLabels: [
          {
            valueName: new Date(
              parsedQuery['resourcePlan.date_to'].value,
            ).toLocaleDateString(),
            valueId: parsedQuery['resourcePlan.date_to'].value,
          },
        ],
      },
      ...(parsedQuery['issue.project_list']?.value
        ? {
            'issue.project_list': {
              label: 'ProjectList',
              value: 'issue.project_list',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['issue.project_list'].value.map(val => ({
                valueId: val,
              })),
              valueName: 'ProjectList',
              fieldType: 'project',
            },
          }
        : {}),
      ...(parsedQuery['resourcePlan.business_unit']?.value
        ? {
            'resourcePlan.business_unit': {
              label: getLocalString('resourcePlan', 'labels', 'businessUnit'),
              value: 'resourcePlan.business_unit',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['resourcePlan.business_unit'].value.map(
                val => ({
                  valueId: val,
                }),
              ),
              valueName: getLocalString(
                'resourcePlan',
                'labels',
                'businessUnit',
              ),
              fieldType: 'list',
            },
          }
        : {}),
      ...(parsedQuery['resourcePlan.user_list']?.value
        ? {
            'resourcePlan.user_list': {
              label: 'UserList',
              value: 'resourcePlan.user_list',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['resourcePlan.user_list'].value.map(
                val => ({
                  valueId: val,
                }),
              ),
              valueName: 'UserList',
              fieldType: 'user_list',
            },
          }
        : {}),
      ...(parsedQuery['resourcePlan.contract_issues']?.value
        ? {
            'resourcePlan.contract_issues': {
              label: 'Contract Issue subject',
              value: 'resourcePlan.contract_issues',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['resourcePlan.contract_issues'].value.map(
                val => ({
                  valueId: val,
                }),
              ),
              valueName: 'Contract Issue subject',
              fieldType: 'contract_issues',
            },
          }
        : {}),
      ...(parsedQuery['resourcePlan.issues']?.value
        ? {
            'resourcePlan.issues': {
              label: 'Issue subject',
              value: 'resourcePlan.issues',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['resourcePlan.issues'].value.map(val => ({
                valueId: val,
              })),
              valueName: 'Issue subject',
              fieldType: 'issues',
            },
          }
        : {}),
      ...(parsedQuery['resourcePlan.department']?.value
        ? {
            'resourcePlan.department': {
              label: getLocalString('resourcePlan', 'labels', 'department'),
              value: 'resourcePlan.department',
              type: 'eq',
              notParsedType: 'eq',
              subLabels: parsedQuery['resourcePlan.department'].value.map(
                val => ({
                  valueId: val,
                }),
              ),
              valueName: getLocalString('resourcePlan', 'labels', 'department'),
              fieldType: 'list',
            },
          }
        : {}),
    },
    chosenSecondStepItems: {},
  });
  useEffect(() => {
    setFilterValues({
      chosenFirstStepItems: {
        'resourcePlan.group_by': {
          valueName: 'GroupBy',
          type: 'eq',
          fieldType: 'group_by',
          subLabels: [
            {
              valueId: parsedQuery['resourcePlan.group_by'].value,
              isChecked: true,
            },
          ],
        },
        'resourcePlan.date_from': {
          valueName: 'DateFrom',
          type: 'gte',
          label: 'DateFrom',
          notParsedType: 'gte',
          fieldType: 'date',
          value: 'resourcePlan.date_from',
          subLabels: [
            {
              valueName: new Date(
                parsedQuery['resourcePlan.date_from'].value,
              ).toLocaleDateString(),
              valueId: parsedQuery['resourcePlan.date_from'].value,
            },
          ],
        },
        'resourcePlan.date_to': {
          valueName: 'DateTo',
          type: 'lte',
          label: 'DateTo',
          notParsedType: 'lte',
          fieldType: 'date',
          value: 'resourcePlan.date_to',
          subLabels: [
            {
              valueName: new Date(
                parsedQuery['resourcePlan.date_to'].value,
              ).toLocaleDateString(),
              valueId: parsedQuery['resourcePlan.date_to'].value,
            },
          ],
        },
      },
      chosenSecondStepItems: {},
    });
  }, [refreshKey]);
  const groupBySubLabels =
    filterValues?.chosenFirstStepItems['resourcePlan.group_by']?.subLabels;
  const currentValueID = groupBySubLabels?.[0]?.valueId; // предполагая, что вам нужно значение из первого элемента subLabels
  const [prevValueID, setPrevValueID] = useState(null);

  const setFilterItems = useCallback(newFirstStepChosenValues => {
    setFilterValues(prevState => {
      Object.keys(newFirstStepChosenValues).map(value => {
        if (value.at(-1) === '_') {
          delete prevState.chosenFirstStepItems[value.slice(0, -1)];
        }
      });
      return {
        chosenFirstStepItems: {
          ...prevState.chosenFirstStepItems,
          ...newFirstStepChosenValues,
        },
      };
    });
  }, []);

  const removeCurrentItemAndUpdateFilters = (
    item,
    filterValues,
    setFilterValues,
    forceUpdate,
  ) => {
    const chosenItemsWithoutCurrent = Object.entries(
      filterValues.chosenFirstStepItems,
    ).reduce((acc, [key, value]) => {
      if (value.valueName === 'Groupby') {
        return { ...acc, ['resourcePlan.group_by']: value };
      } else if (item.value !== value.value) {
        return { ...acc, [value.value]: value };
      }
      return acc;
    }, {});

    setFilterValues(prevState => ({
      chosenFirstStepItems: { ...chosenItemsWithoutCurrent },
    }));
  };
  useEffect(() => {
    if (Object.keys(filterValues.chosenFirstStepItems)?.length > 0) {
      let cleanedQuery = Object.entries(filterValues.chosenFirstStepItems)
        .map(([key, item]) => {
          if (item.type === 'null') {
            return `${item.value};${item.type}`;
          }
          if (item.type === 'all') {
            return;
          }
          if (key.split('').at(-1) !== '_') {
            if (item.fieldType === 'group_by') {
              item.type = 'eq';
              return item.subLabels?.length > 0 &&
                item.subLabels[0].valueId !== ''
                ? `${key};${item.type};${item.subLabels[0].valueId}`
                : null;
            }

            if (item.type === '') {
              removeCurrentItemAndUpdateFilters(
                item,
                filterValues,
                setFilterValues,
                forceUpdate,
              );
              return;
            }
            const values =
              item.subLabels && item.subLabels.length > 0
                ? item.subLabels.map(subItem => subItem.valueId)
                : null;
            if (values !== undefined && values !== null) {
              return values.length > 1 && values[1] !== ''
                ? `${key};${item.type};${values.join('||')}`
                : values[0] !== ''
                  ? `${key};${item.type};${values[0]}`
                  : null;
            }
          }
        })
        .filter(item => item);
      cleanedQuery =
        cleanedQuery?.length > 0
          ? cleanedQuery?.length > 1
            ? cleanedQuery.join('$$')
            : cleanedQuery[0]
          : 'filters';
      if (cleanedQuery) {
        cleanedQuery = cleanedQuery.replace('date_from;eq', 'date_from;gte');
        cleanedQuery = cleanedQuery.replace('date_to;eq', 'date_to;lte');
      }
      if (query != cleanedQuery) {
        setQuery(cleanedQuery);
      }
      if (prevValueID !== null && currentValueID !== prevValueID) {
        dispatch(setResplanTableRows({ fields: [], rows: [] }));
        dispatch(clearResplanTableRows());
        dispatch(setStopLoadingRespanTableBySocket(true));
        setFetchedQuery(cleanedQuery);
      }

      setPrevValueID(currentValueID);
    }
  }, [filterValues, setFilterValues]);

  return (
    <>
      <Grid
        sx={{
          alignItems: 'center',
          display: 'inline-flex',
          marginLeft: 0,
        }}
      >
        {topFiltersList.map((filter, index) => {
          const newFirstStepValues = {
            value: filter.fieldId,
            valueName: filter.fieldName,
            fieldType: filter.fieldType,
            type: filter.fieldType !== 'text' ? 'eq' : 'in',
          };
          let prevFirstStepItems;
          if (filter.field == 'group_by') {
            prevFirstStepItems = {
              'resourcePlan.group_by': {
                valueName: 'GroupBy',
                type: 'eq',
                fieldType: 'group_by',
                subLabels: [
                  {
                    valueId: parsedQuery['resourcePlan.group_by'].value,
                    isChecked: true,
                  },
                ],
              },
            };
            filter.values[1] = {
              isChecked: true,
              valueId: '1',
              valueName: getLocalString('resourcePlan', 'labels', 'resource'),
            };
          }
          if (filter.field == 'date_from') {
            prevFirstStepItems = {
              'resourcePlan.date_from': {
                valueName: 'DateFrom',
                type: 'gte',
                label: 'DateFrom',
                notParsedType: 'gte',
                fieldType: 'date',
                value: 'resourcePlan.date_from',
                subLabels: [
                  {
                    valueName: new Date(
                      parsedQuery['resourcePlan.date_from'].value,
                    ).toLocaleDateString(),
                    valueId: parsedQuery['resourcePlan.date_from'].value,
                  },
                ],
              },
            };
          }
          if (filter.field == 'date_to') {
            prevFirstStepItems = {
              'resourcePlan.date_to': {
                valueName: 'DateTo',
                type: 'lte',
                label: 'DateTo',
                notParsedType: 'lte',
                fieldType: 'date',
                value: 'resourcePlan.date_to',
                subLabels: [
                  {
                    valueName: new Date(
                      parsedQuery['resourcePlan.date_to'].value,
                    ).toLocaleDateString(),
                    valueId: parsedQuery['resourcePlan.date_to'].value,
                  },
                ],
              },
            };
          }
          return (
            <Grid key={index}>
              <StyledFilterTopRow
                key={index}
                sx={{
                  width:
                    filter.field == 'group_by'
                      ? 'auto'
                      : filter.fieldType == 'text' ||
                          filter.fieldType == 'string' ||
                          filter.fieldType == 'bool' ||
                          filter.fieldType == 'boolean' ||
                          filter.fieldType == 'int' ||
                          filter.fieldType == 'float'
                        ? '100%'
                        : '190px',
                  marginBottom: '22px',
                  marginRight: index === topFiltersList.length - 2 ? 2 : 5,
                }}
              >
                <CustomFilter
                  secondStepItems={filter.values || []}
                  chosenSecondStepItems={filterValues.chosenSecondStepItems}
                  setValues={setFilterItems}
                  defaultStep={1}
                  firstStepValues={newFirstStepValues}
                  chosenFirstStepItems={prevFirstStepItems}
                />
              </StyledFilterTopRow>
            </Grid>
          );
        })}
      </Grid>
      <Grid container spacing={0}>
        {restFiltersList.map((filter, index) => {
          const newFirstStepValues = {
            value: filter.fieldId,
            valueName: filter.fieldName,
            fieldType: filter.fieldType,
            type: filter.fieldType !== 'text' ? 'eq' : 'in',
          };
          if (filter.fieldType == 'user_list' && filter?.values[0]) {
            filter.values[0].valueName = getLocalString('labels', 'me');
          }
          let prevFirstStepItems;
          const secondStepItemsValues = filter.values || [];
          if (
            parsedQuery['issue.project_list']?.value &&
            filter.field == 'project_list'
          ) {
            prevFirstStepItems = {
              'issue.project_list': {
                label: 'ProjectList',
                value: 'issue.project_list',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery['issue.project_list'].value.map(val => ({
                  valueId: val,
                })),
                valueName: 'ProjectList',
                fieldType: 'project',
              },
            };
          }
          if (
            parsedQuery['resourcePlan.business_unit']?.value &&
            filter.field == 'business_unit'
          ) {
            prevFirstStepItems = {
              'resourcePlan.business_unit': {
                label: getLocalString('resourcePlan', 'labels', 'businessUnit'),
                value: 'resourcePlan.business_unit',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery['resourcePlan.business_unit'].value.map(
                  val => ({
                    valueId: val,
                  }),
                ),
                valueName: getLocalString(
                  'resourcePlan',
                  'labels',
                  'businessUnit',
                ),
                fieldType: 'list',
              },
            };
          }
          if (
            parsedQuery['resourcePlan.user_list']?.value &&
            filter.field == 'user_list'
          ) {
            prevFirstStepItems = {
              'resourcePlan.user_list': {
                label: 'UserList',
                value: 'resourcePlan.user_list',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery['resourcePlan.user_list'].value.map(
                  val => ({
                    valueId: val,
                  }),
                ),
                valueName: 'UserList',
                fieldType: 'user_list',
              },
            };
          }
          if (
            parsedQuery['resourcePlan.contract_issues']?.value &&
            filter.field == 'contract_issues'
          ) {
            prevFirstStepItems = {
              'resourcePlan.contract_issues': {
                label: 'Contract Issue subject',
                value: 'resourcePlan.contract_issues',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery[
                  'resourcePlan.contract_issues'
                ].value.map(val => ({
                  valueId: val,
                })),
                valueName: 'Contract Issue subject',
                fieldType: 'contract_issues',
              },
            };
          }
          if (
            parsedQuery['resourcePlan.issues']?.value &&
            filter.field == 'issues'
          ) {
            prevFirstStepItems = {
              'resourcePlan.issues': {
                label: 'Issue subject',
                value: 'resourcePlan.issues',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery['resourcePlan.issues'].value.map(
                  val => ({
                    valueId: val,
                  }),
                ),
                valueName: 'Issue subject',
                fieldType: 'issues',
              },
            };
          }
          if (
            parsedQuery['resourcePlan.department']?.value &&
            filter.field == 'department'
          ) {
            prevFirstStepItems = {
              'resourcePlan.department': {
                label: getLocalString('resourcePlan', 'labels', 'department'),
                value: 'resourcePlan.department',
                type: 'eq',
                notParsedType: 'eq',
                subLabels: parsedQuery['resourcePlan.department'].value.map(
                  val => ({
                    valueId: val,
                  }),
                ),
                valueName: getLocalString(
                  'resourcePlan',
                  'labels',
                  'department',
                ),
                fieldType: 'list',
              },
            };
          }

          return (
            <Grid key={index}>
              <StyledFilterWrap
                sx={{
                  width:
                    filter.field == 'group_by'
                      ? 'auto'
                      : filter.fieldType == 'text' ||
                          filter.fieldType == 'string' ||
                          filter.fieldType == 'bool' ||
                          filter.fieldType === 'boolean' ||
                          filter.fieldType === 'int' ||
                          filter.fieldType == 'float'
                        ? '100%'
                        : '177px',
                  marginBottom: 3,
                  marginRight: 5,
                }}
              >
                <CustomFilter
                  key={index + filter.values?.length + refreshKey}
                  parsedQuery={parsedQuery}
                  secondStepItems={secondStepItemsValues}
                  chosenSecondStepItems={filterValues.chosenSecondStepItems}
                  setValues={setFilterItems}
                  defaultStep={1}
                  resplan={true}
                  firstStepValues={newFirstStepValues}
                  chosenFirstStepItems={prevFirstStepItems}
                />
              </StyledFilterWrap>
            </Grid>
          );
        })}
        {hasNewFilters ? (
          <Tooltip
            title={applyTooltip}
            placement='top'
            slotProps={{
              popper: { component: StyledCommentTooltip },
            }}
          >
            <StyledConfirmButton
              sx={{
                backgroundColor: '#6C7AD9',
              }}
              variant='defaultBluePrimaryWithIcon'
              onClick={() => {
                setQuery(query);
                dispatch(setResplanTableRows({ fields: [], rows: [] }));
                dispatch(clearResplanTableRows());
                dispatch(setStopLoadingRespanTableBySocket(true));
                setFetchedQuery(query);
              }}
              disableRipple
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <DoneIcon width={20} height={20} viewBox='0 0 20 20' />
              </Box>
            </StyledConfirmButton>
          </Tooltip>
        ) : (
          <StyledConfirmButton
            variant='defaultGreyPrimaryWithIcon'
            disabled
            disableRipple
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <DoneIcon
                color={'#7174AC'}
                width={20}
                height={20}
                viewBox='0 0 20 20'
              />
            </Box>
          </StyledConfirmButton>
        )}
      </Grid>
    </>
  );
};
export default ResourcePlanFilter;
